/**
 * "Saada kliendile": who gets the monthly report and an optional note.
 *
 * The addresses are the client's own and its contacts' (the server refuses
 * any other), ticked in a list rather than typed, so a typo cannot send a
 * client's report to a stranger. The client's own address and the primary
 * contacts start ticked; at most five. A report that already went out says so
 * and when, and sending it again is a deliberate second press of the same
 * button. A report that only reached the test inbox says that instead: the
 * client never got it, so this is its first real send.
 *
 * A refusal that means the row changed under the dialog (somebody else sent
 * or is building it) closes the dialog and has the card read the row again
 * (`onRefused`); any other refusal stays in the dialog to be fixed there.
 *
 * The mail leaves from the CRM mailbox with the PDF attached, and the
 * client's e-mail thread shows it.
 */
import { useState } from "react";
import { Loader2, Send } from "lucide-react";
import { Textarea } from "@wa/ui";
import { monitorErrorKey } from "../../../../lib/monitoring";
import {
  MONTHLY_NOTE_MAX,
  MONTHLY_SEND_TO_MAX,
  defaultRecipients,
  monthLabel,
  refusalMeansCardIsBehind,
  sendMonthReport,
  sentOnlyToTestInbox,
  toggleRecipient,
  type MonthReportRowView,
  type MonthlyRecipientView,
  type MonthlySendResponse,
} from "../../../../lib/monitoringMonthly";
import { Btn, CheckField, Modal, fmtDateTime } from "../../../ui";
import { Field } from "../../_shared/formBits";
import type { T } from "../words";

export function SendDialog({
  monitorId,
  report,
  recipients,
  locale,
  t,
  onClose,
  onSent,
  onRefused,
}: {
  monitorId: string;
  report: MonthReportRowView;
  recipients: MonthlyRecipientView[];
  locale: string;
  t: T;
  onClose: () => void;
  onSent: (res: MonthlySendResponse) => void;
  /** The row changed under the dialog: the card says why, closes it and reads the row again. */
  onRefused: (message: string) => void;
}) {
  const [picked, setPicked] = useState<string[]>(() => defaultRecipients(recipients));
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const heading = t("monitoring.monthly.send.title", { month: monthLabel(report.month, locale) });
  const testOnly = sentOnlyToTestInbox(report);

  async function send() {
    if (busy || picked.length === 0) return;
    setBusy(true);
    setError(null);
    try {
      const res = await sendMonthReport(monitorId, report.month, {
        to: picked,
        ...(note.trim() ? { note: note.trim() } : {}),
        ...(report.sentAt ? { resend: true } : {}),
      });
      onSent(res);
      onClose();
    } catch (err) {
      const key = monitorErrorKey(err);
      if (refusalMeansCardIsBehind(key)) onRefused(t(`monitoring.error.${key}`));
      else setError(t(`monitoring.error.${key}`));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal title={heading} label={heading} onClose={onClose} className="max-w-[560px]">
      {report.sentAt ? (
        <p className="mb-3 text-[13px] text-[var(--color-warning)]">
          {t(testOnly ? "monitoring.monthly.send.alreadyTest" : "monitoring.monthly.send.already", { when: fmtDateTime(report.sentAt) })}
        </p>
      ) : null}

      <Field label={t("monitoring.monthly.send.to")} hint={t("monitoring.monthly.send.toHint", { max: MONTHLY_SEND_TO_MAX })}>
        {recipients.length === 0 ? (
          <p className="crm-sub text-[13px]">{t("monitoring.monthly.send.noRecipients")}</p>
        ) : (
          <div className="grid gap-2">
            {recipients.map((r) => (
              <CheckField
                key={r.email}
                checked={picked.includes(r.email)}
                disabled={!picked.includes(r.email) && picked.length >= MONTHLY_SEND_TO_MAX}
                onChange={(on) => setPicked((now) => toggleRecipient(now, r.email, on))}
                label={
                  <>
                    <span className="font-semibold">{r.name}</span>
                    <span className="crm-mut"> {r.email}</span>
                  </>
                }
              />
            ))}
          </div>
        )}
      </Field>

      <Field label={t("monitoring.monthly.send.note")} hint={t("monitoring.monthly.send.noteHint")}>
        <Textarea
          value={note}
          onChange={(e) => setNote(e.target.value.slice(0, MONTHLY_NOTE_MAX))}
          rows={4}
          maxLength={MONTHLY_NOTE_MAX}
          aria-label={t("monitoring.monthly.send.note")}
        />
      </Field>

      {error ? (
        <p className="mt-3 text-[13px] text-[var(--color-destructive)]" role="alert">
          {error}
        </p>
      ) : null}

      <div className="crm-m-foot">
        <Btn type="button" onClick={onClose} disabled={busy}>
          {t("common.cancel")}
        </Btn>
        <Btn type="button" tone="dark" disabled={busy || picked.length === 0} onClick={() => void send()}>
          {busy ? <Loader2 className="size-4 animate-spin" /> : <Send className="size-[15px]" strokeWidth={1.8} />}
          {t(report.sentAt && !testOnly ? "monitoring.monthly.send.again" : "monitoring.monthly.send.button")}
        </Btn>
      </div>
    </Modal>
  );
}
